import { useEffect, useMemo, useState } from "react";
import t from "@/lang/lang";
import {
  GfsAreaAssessment,
  GfsAssessment,
  GfsAreaSnapshot,
} from "@/services/backend/gfs/assessment/types";
import {
  countAssessmentMonitorsBySite,
  GFS_AREA_TYPE_INSIDE,
  GFS_AREA_TYPE_OUTSIDE,
  GfsAreaSelectorComboBox,
  GfsAreaType,
  GfsBuildingSelector,
  GfsLevelSelector,
} from "@/services/backend/gfs/assessment/components/gfs-site-selector-components";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import { AssessmentField } from "@/services/backend/gfs/assessment/components/assessment-field";
import { BuildingWithoutAreas } from "@/services/backend/buildings/types/building";

type GfsSiteSelectionScope = {
  buildingId: string;
  levelId: string;
  areaType: GfsAreaType;
};

type GfsAssessmentSelectionCache = GfsSiteSelectionScope;

export function SiteSelection({
  assessmentId,
  buildings,
  areaAssessments,
  onChange,
  onAreaSelect,
  onPersist,
  onScopeChange,
  customerBuildings,
}: {
  assessmentId: string;
  buildings: GfsAssessment["buildings"];
  areaAssessments: GfsAreaAssessment[];
  onChange: (next: GfsAssessment["buildings"]) => void;
  onAreaSelect: (area: GfsAreaSnapshot) => void;
  onPersist: (next: GfsAssessment["buildings"]) => Promise<boolean>;
  onScopeChange: (scope: {
    buildingId: string;
    levelId: string;
    areaType: GfsAreaType;
  }) => void;
  customerBuildings?: BuildingWithoutAreas[];
}) {
  const [selectedBuildingId, setSelectedBuildingId] = useState("");
  const [selectedLevelId, setSelectedLevelId] = useState("");
  const [areaType, setAreaType] = useState<GfsAreaType>(GFS_AREA_TYPE_INSIDE);
  const cacheStorageKey = useMemo(
    () => gfsAssessmentSelectionStorageKey(assessmentId),
    [assessmentId],
  );
  const selectedBuilding = buildings.find(
    (building) => building.id === selectedBuildingId,
  );
  const visibleLevels = (selectedBuilding?.levels ?? []).filter(
    (level) => !level.type || level.type === areaType,
  );
  const selectedLevel = visibleLevels.find(
    (level) => level.id === selectedLevelId,
  );
  const visibleAreas = (selectedBuilding?.areas ?? []).filter(
    (area) =>
      (area.levelId ?? "") === selectedLevelId && area.type === areaType,
  );
  const monitorCounts = countAssessmentMonitorsBySite(
    buildings,
    areaAssessments,
  );

  useEffect(() => {
    const currentSelection = {
      buildingId: selectedBuildingId,
      levelId: selectedLevelId,
      areaType,
    };
    if (isValidGfsSiteSelection(buildings, currentSelection)) {
      return;
    }

    const cachedSelection = readGfsAssessmentSelectionCache(cacheStorageKey);
    const nextSelection =
      cachedSelection && isValidGfsSiteSelection(buildings, cachedSelection)
        ? cachedSelection
        : selectGfsSiteScopeByMostMonitors(buildings, areaAssessments);

    if (selectedBuildingId !== nextSelection.buildingId) {
      setSelectedBuildingId(nextSelection.buildingId);
    }
    if (selectedLevelId !== nextSelection.levelId) {
      setSelectedLevelId(nextSelection.levelId);
    }
    if (areaType !== nextSelection.areaType) {
      setAreaType(nextSelection.areaType);
    }
  }, [
    areaAssessments,
    areaType,
    buildings,
    cacheStorageKey,
    selectedBuildingId,
    selectedLevelId,
  ]);

  useEffect(() => {
    onScopeChange({
      buildingId: selectedBuildingId,
      levelId: selectedLevelId,
      areaType,
    });
  }, [areaType, onScopeChange, selectedBuildingId, selectedLevelId]);

  useEffect(() => {
    if (!selectedBuildingId) {
      return;
    }

    writeGfsAssessmentSelectionCache(cacheStorageKey, {
      buildingId: selectedBuildingId,
      levelId: selectedLevelId,
      areaType,
    });
  }, [areaType, cacheStorageKey, selectedBuildingId, selectedLevelId]);

  useEffect(() => {
    if (
      selectedLevelId &&
      !visibleLevels.some((level) => level.id === selectedLevelId)
    ) {
      setSelectedLevelId(
        selectedBuilding
          ? selectGfsLevelIdByMostMonitors(
              selectedBuilding,
              areaType,
              areaAssessments,
            )
          : "",
      );
    }
  }, [
    areaAssessments,
    areaType,
    selectedBuilding,
    selectedLevelId,
    visibleLevels,
  ]);

  const updateSelectedBuilding = (
    update: (
      building: GfsAssessment["buildings"][number],
    ) => GfsAssessment["buildings"][number],
  ) => {
    if (!selectedBuilding) return undefined;
    const nextBuildings = buildings.map((building) =>
      building.id === selectedBuilding.id ? update(building) : building,
    );
    onChange(nextBuildings);
    return nextBuildings;
  };

  const selectBuilding = (id: string) => {
    const nextBuilding = buildings.find((building) => building.id === id);
    setSelectedBuildingId(id);
    setSelectedLevelId(
      nextBuilding
        ? selectGfsLevelIdByMostMonitors(
            nextBuilding,
            areaType,
            areaAssessments,
          )
        : "",
    );
  };

  const handleBuildingCreated = async (
    building: GfsAssessment["buildings"][number],
  ) => {
    const nextBuildings = [...buildings, building];
    onChange(nextBuildings);
    setSelectedBuildingId(building.id);
    setSelectedLevelId("");
    return onPersist(nextBuildings);
  };

  const handleLevelCreated = async (
    level: GfsAssessment["buildings"][number]["levels"][number],
  ) => {
    const nextBuildings = updateSelectedBuilding((building) => ({
      ...building,
      levels: [...building.levels, level],
    }));
    setSelectedLevelId(level.id);
    if (nextBuildings) {
      return onPersist(nextBuildings);
    }
    return false;
  };

  const handleAreaCreated = async (
    area: GfsAssessment["buildings"][number]["areas"][number],
  ) => {
    const nextBuildings = updateSelectedBuilding((building) => ({
      ...building,
      areas: [...building.areas, area],
    }));
    if (nextBuildings) {
      onAreaSelect(area);
      return onPersist(nextBuildings);
    }
    return false;
  };

  return (
    <section className="space-y-5">
      <div className="flex flex-wrap items-start gap-2">
        <GfsBuildingSelector
          buildings={buildings}
          selectedBuilding={selectedBuilding}
          handleBuildingSelect={selectBuilding}
          onBuildingCreateCallback={handleBuildingCreated}
          monitorCountByBuildingId={monitorCounts.buildingCounts}
          assessmentId={assessmentId}
          customerBuildings={customerBuildings}
          onCustomerBuildingImported={(nextAssessment, importedBuildingId) => {
            onChange(nextAssessment.buildings);
            const importedBuilding = nextAssessment.buildings.find(
              (building) => building.id === importedBuildingId,
            );
            if (!importedBuilding) return;
            setSelectedBuildingId(importedBuilding.id);
            const importedScope = selectGfsSiteScopeByMostMonitors(
              [importedBuilding],
              areaAssessments,
            );
            setAreaType(importedScope.areaType);
            setSelectedLevelId(importedScope.levelId);
          }}
        />
        <AssessmentField label={t("Bereichsart")}>
          <Select
            value={areaType}
            onValueChange={(value) => {
              const nextAreaType = value as GfsAreaType;
              setAreaType(nextAreaType);
              if (selectedBuilding) {
                setSelectedLevelId(
                  selectGfsLevelIdByMostMonitors(
                    selectedBuilding,
                    nextAreaType,
                    areaAssessments,
                  ),
                );
              }
            }}
          >
            <SelectTrigger className="h-10 w-full max-w-[250px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={GFS_AREA_TYPE_INSIDE}>
                {t("Innenbereich")}
              </SelectItem>
              <SelectItem value={GFS_AREA_TYPE_OUTSIDE}>
                {t("Außenbereich")}
              </SelectItem>
            </SelectContent>
          </Select>
        </AssessmentField>
        <GfsLevelSelector
          levels={visibleLevels}
          selectedLevel={selectedLevel}
          handleLevelSelect={setSelectedLevelId}
          buildingId={selectedBuilding?.id}
          createLevelType={areaType}
          onLevelCreatedCallback={handleLevelCreated}
          monitorCountByLevelId={monitorCounts.levelCounts}
          noLevelMonitorCount={
            selectedBuilding
              ? (monitorCounts.noLevelCounts[
                  `${selectedBuilding.id}:${areaType}`
                ] ?? 0)
              : 0
          }
          isNoLevelSelected={!selectedLevelId}
        />
      </div>
      <div>
        <GfsAreaSelectorComboBox
          areas={visibleAreas}
          onAreaSelect={onAreaSelect}
          buildingId={selectedBuilding?.id}
          levelId={selectedLevelId}
          areaType={areaType}
          triggerLabel={
            areaType === GFS_AREA_TYPE_INSIDE
              ? t("Innenbereich wählen oder anlegen")
              : t("Außenbereich wählen oder anlegen")
          }
          onAreaCreate={handleAreaCreated}
        />
      </div>
    </section>
  );
}

function gfsAssessmentSelectionStorageKey(assessmentId: string): string {
  return `gfs.assessment.selection.${assessmentId}`;
}

function readGfsAssessmentSelectionCache(
  storageKey: string,
): GfsAssessmentSelectionCache | undefined {
  if (typeof window === "undefined") {
    return undefined;
  }

  const rawCache = window.localStorage.getItem(storageKey);
  if (!rawCache) {
    return undefined;
  }

  try {
    const cacheValue = JSON.parse(
      rawCache,
    ) as Partial<GfsAssessmentSelectionCache>;
    if (!cacheValue.buildingId) {
      return undefined;
    }
    return {
      buildingId: cacheValue.buildingId,
      levelId: cacheValue.levelId ?? "",
      areaType: toGfsAreaType(cacheValue.areaType),
    };
  } catch {
    return undefined;
  }
}

function writeGfsAssessmentSelectionCache(
  storageKey: string,
  cacheValue: GfsAssessmentSelectionCache,
): void {
  if (typeof window === "undefined") {
    return;
  }

  window.localStorage.setItem(storageKey, JSON.stringify(cacheValue));
}

function toGfsAreaType(value: unknown): GfsAreaType {
  if (value === GFS_AREA_TYPE_OUTSIDE) {
    return GFS_AREA_TYPE_OUTSIDE;
  }
  return GFS_AREA_TYPE_INSIDE;
}

function isValidGfsSiteSelection(
  buildings: GfsAssessment["buildings"],
  selection: GfsSiteSelectionScope,
): boolean {
  const building = buildings.find(
    (candidate) => candidate.id === selection.buildingId,
  );
  if (!building) {
    return false;
  }

  const visibleLevels = building.levels.filter(
    (level) => !level.type || level.type === selection.areaType,
  );
  if (!selection.levelId) {
    if (visibleLevels.length === 0) {
      return true;
    }
    return building.areas.some(
      (area) =>
        !area.levelId && (!area.type || area.type === selection.areaType),
    );
  }

  return visibleLevels.some((level) => level.id === selection.levelId);
}

function selectGfsSiteScopeByMostMonitors(
  buildings: GfsAssessment["buildings"],
  areaAssessments: GfsAreaAssessment[],
): GfsSiteSelectionScope {
  const defaultSelection: GfsSiteSelectionScope = {
    buildingId: "",
    levelId: "",
    areaType: GFS_AREA_TYPE_INSIDE,
  };
  if (buildings.length === 0) {
    return defaultSelection;
  }

  const areaMonitorCounts = areaAssessments.reduce<Record<string, number>>(
    (counts, areaAssessment) => ({
      ...counts,
      [areaAssessment.areaId]: areaAssessment.monitors.reduce(
        (sum, monitor) => sum + Math.max(monitor.amount || 0, 0),
        0,
      ),
    }),
    {},
  );

  const bestAreaSelection = buildings.reduce<
    | {
        buildingId: string;
        levelId: string;
        areaType: GfsAreaType;
        monitorCount: number;
      }
    | undefined
  >((currentSelection, building) => {
    return building.areas.reduce<typeof currentSelection>(
      (selectedArea, area) => {
        const monitorCount = areaMonitorCounts[area.id] ?? 0;
        if (!selectedArea || monitorCount > selectedArea.monitorCount) {
          return {
            buildingId: building.id,
            levelId: area.levelId ?? "",
            areaType: toGfsAreaType(area.type),
            monitorCount,
          };
        }
        return selectedArea;
      },
      currentSelection,
    );
  }, undefined);

  if (bestAreaSelection) {
    return {
      buildingId: bestAreaSelection.buildingId,
      levelId: bestAreaSelection.levelId,
      areaType: bestAreaSelection.areaType,
    };
  }

  const monitorCounts = countAssessmentMonitorsBySite(
    buildings,
    areaAssessments,
  );
  const selectedBuilding = buildings.reduce((currentBuilding, building) => {
    const currentCount = monitorCounts.buildingCounts[currentBuilding.id] ?? 0;
    const count = monitorCounts.buildingCounts[building.id] ?? 0;
    return count > currentCount ? building : currentBuilding;
  }, buildings[0]);
  const selectedLevel = selectedBuilding.levels[0];

  return {
    buildingId: selectedBuilding.id,
    levelId: selectedLevel?.id ?? "",
    areaType: toGfsAreaType(selectedLevel?.type),
  };
}

function selectGfsLevelIdByMostMonitors(
  building: GfsAssessment["buildings"][number],
  areaType: GfsAreaType,
  areaAssessments: GfsAreaAssessment[],
): string {
  const monitorCounts = countAssessmentMonitorsBySite(
    [building],
    areaAssessments,
  );
  const visibleLevels = building.levels.filter(
    (level) => !level.type || level.type === areaType,
  );
  const noLevelCount =
    monitorCounts.noLevelCounts[`${building.id}:${areaType}`] ?? 0;
  return visibleLevels.reduce(
    (selectedLevelId, level) => {
      const selectedCount = selectedLevelId
        ? (monitorCounts.levelCounts[selectedLevelId] ?? 0)
        : noLevelCount;
      const count = monitorCounts.levelCounts[level.id] ?? 0;
      return count > selectedCount ? level.id : selectedLevelId;
    },
    noLevelCount > 0 ? "" : (visibleLevels[0]?.id ?? ""),
  );
}
